import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LinearProgress from '@mui/material/LinearProgress';
import PersonIcon from '@mui/icons-material/PersonOutlineOutlined';
import BuildIcon from '@mui/icons-material/BuildOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import Link from '@mui/material/Link';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { fmtInt } from '../lib/format';
import { computeCustomerBugStats, cpsTicketId } from '../lib/customer-bugs';
import { isDone } from '../lib/status';
import { StatusChip } from './StatusChip';
import type { Issue } from '../types/issue';

// Gemeinsam mit DuplicateTickets: dieselbe JIRA-Base-URL im localStorage,
// damit der User sie nur einmal pflegen muss.
const JIRA_URL_KEY = 'v2.jira.baseUrl';
function loadJiraBaseUrl(): string {
  if (typeof window === 'undefined') return 'https://schueco.atlassian.net';
  return (localStorage.getItem(JIRA_URL_KEY) || 'https://schueco.atlassian.net').replace(/\/+$/, '');
}
function jiraUrl(base: string, issueKey: string): string {
  return `${base}/browse/${encodeURIComponent(issueKey)}`;
}

type Scope = 'all' | 'open' | 'done';

export function CustomerBugs() {
  const rows = useFilteredRows() as Issue[];
  const [scope, setScope] = useState<Scope>('all');
  const [teamFocus, setTeamFocus] = useState<string | null>(null);

  const stats = useMemo(() => computeCustomerBugStats(rows), [rows]);

  const scopedBugs = useMemo(() => {
    const out = rows.filter(r => r['Issue Type'] === 'Bug');
    if (scope === 'open') return out.filter(r => !isDone(String(r['Status'] ?? '')));
    if (scope === 'done') return out.filter(r => isDone(String(r['Status'] ?? '')));
    return out;
  }, [rows, scope]);

  if (stats.totalBugs === 0) {
    return (
      <Alert severity="info" sx={{ borderRadius: 3 }}>
        Keine Bugs in der aktuellen Auswahl. Filter oben lockern oder anderen Team-Filter setzen.
      </Alert>
    );
  }

  const pctCustomer = stats.totalBugs > 0 ? (stats.customerBugs / stats.totalBugs) * 100 : 0;

  return (
    <Stack spacing={2}>
      {/* KPI-Zeile */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Card sx={{ flex: 1, minWidth: 220, borderLeft: '4px solid #1976d2' }}>
          <CardContent sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PersonIcon color="primary" />
              <Typography variant="overline" color="text.secondary">Kunden-Bugs</Typography>
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              {fmtInt(stats.customerBugs)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pctCustomer.toFixed(1)}% aller Bugs · {fmtInt(stats.openCustomer)} offen · {fmtInt(stats.doneCustomer)} done
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 220, borderLeft: '4px solid #616161' }}>
          <CardContent sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <BuildIcon />
              <Typography variant="overline" color="text.secondary">Interne Bugs</Typography>
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              {fmtInt(stats.internalBugs)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(100 - pctCustomer).toFixed(1)}% aller Bugs · {fmtInt(stats.openInternal)} offen · {fmtInt(stats.doneInternal)} done
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 220, bgcolor: 'action.hover' }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="overline" color="text.secondary">Σ Bugs</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              {fmtInt(stats.totalBugs)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Balken: Anteil Kunden-Bugs
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pctCustomer}
              sx={{
                mt: 1,
                height: 8,
                borderRadius: 1,
                bgcolor: '#61616120',
                '& .MuiLinearProgress-bar': { bgcolor: '#1976d2' },
              }}
            />
          </CardContent>
        </Card>
      </Stack>

      {/* Aufteilung pro Team */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Aufteilung pro Team
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Ein Bug zählt als <b>Kunden-Bug</b>, wenn das JIRA-Feld „CPS Ticket ID" einen Wert hat (Verknüpfung zum Support-Ticket, z.&nbsp;B. SD-23008647).
            Sonst intern. Klick auf Team → filtert die Detail-Tabelle unten.
          </Typography>
          <TableContainer sx={{ maxHeight: 380 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Bugs total</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Kunde</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Intern</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Anteil Kunde</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">offen (K / I)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.perTeam.map(t => (
                  <TableRow
                    key={t.team}
                    hover
                    selected={teamFocus === t.team}
                    onClick={() => setTeamFocus(teamFocus === t.team ? null : t.team)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{t.team}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                      {fmtInt(t.bugs)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: '#1976d2', fontWeight: 600 }}>
                      {fmtInt(t.customer)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {fmtInt(t.internal)}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                        <Box sx={{ width: 60 }}>
                          <LinearProgress
                            variant="determinate"
                            value={t.ratio * 100}
                            sx={{
                              height: 6,
                              borderRadius: 1,
                              bgcolor: '#61616120',
                              '& .MuiLinearProgress-bar': { bgcolor: '#1976d2' },
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 40 }}>
                          {Math.round(t.ratio * 100)}%
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12, color: 'text.secondary' }}>
                      {fmtInt(t.openCust)} / {fmtInt(t.openInt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Detail-Tabelle: alle Bugs (oder gefilterten Team) */}
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Bug-Liste
                {teamFocus && (
                  <>
                    {' · '}
                    <Chip
                      size="small"
                      label={`Team: ${teamFocus}`}
                      onDelete={() => setTeamFocus(null)}
                      sx={{ height: 20, ml: 0.5 }}
                    />
                  </>
                )}
              </Typography>
            </Box>
            <ToggleButtonGroup
              size="small"
              value={scope}
              exclusive
              onChange={(_, v) => v && setScope(v as Scope)}
            >
              <ToggleButton value="all">alle</ToggleButton>
              <ToggleButton value="open">nur offen</ToggleButton>
              <ToggleButton value="done">nur done</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <BugList rows={scopedBugs} teamFocus={teamFocus} />
        </CardContent>
      </Card>
    </Stack>
  );
}

// ─── Detail-Liste ─────────────────────────────────────────────────────────

function BugList({ rows, teamFocus }: { rows: Issue[]; teamFocus: string | null }) {
  const jiraBase = useMemo(() => loadJiraBaseUrl(), []);
  const list = useMemo(() => {
    let out = rows;
    if (teamFocus) out = out.filter(r => r['Team Name'] === teamFocus);
    // Kunden-Bugs zuerst, dann nach Created DESC (soweit bekannt)
    return out.slice().sort((a, b) => {
      const aCust = cpsTicketId(a) != null ? 0 : 1;
      const bCust = cpsTicketId(b) != null ? 0 : 1;
      if (aCust !== bCust) return aCust - bCust;
      const ac = String(a['Created'] ?? '');
      const bc = String(b['Created'] ?? '');
      return bc.localeCompare(ac);
    });
  }, [rows, teamFocus]);

  const MAX = 200;
  const shown = list.slice(0, MAX);

  return (
    <>
      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>CPS-Verknüpfung</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Summary</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((r) => {
              const cps = cpsTicketId(r);
              const key = String(r['Issue key'] ?? '');
              return (
                <TableRow key={key} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    <Link
                      href={jiraUrl(jiraBase, key)}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}
                    >
                      {key}
                      <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                    </Link>
                  </TableCell>
                  <TableCell>
                    {cps ? (
                      <Chip
                        size="small"
                        icon={<PersonIcon fontSize="small" />}
                        label={cps}
                        color="primary"
                        variant="filled"
                        clickable
                        component="a"
                        href={jiraUrl(jiraBase, cps)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ height: 22, fontFamily: 'monospace', cursor: 'pointer' }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        icon={<BuildIcon fontSize="small" />}
                        label="intern"
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={String(r['Status'] ?? '')} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{String(r['Team Name'] ?? '–')}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {String(r['Priority'] ?? '–')}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, maxWidth: 500 }}>
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(r['Summary'] ?? '')}>
                      {String(r['Summary'] ?? '')}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>
                  Keine Bugs in dieser Auswahl.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {list.length > MAX && (
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
          Anzeige auf {fmtInt(MAX)} Zeilen begrenzt (Σ {fmtInt(list.length)} Bugs). Team-Klick oben oder Scope oben rechts nutzen zum Verkleinern.
        </Typography>
      )}
    </>
  );
}
